import React, { useState } from "react";
import { SxProps, Theme } from "@mui/material/styles";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import { CustomPopper } from "src/components/common/popper/CustomPopper.tsx";
import { useDebounce } from "src/utils/useDebounce.ts";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { mergeSx } from "src/utils/styles.ts";
import { nullIfBlank } from "src/utils/strings.tsx";
import { HorizontalBox } from "../box/HorizontalBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { GoogleGeocodingAddressComponent } from "src/api/generated/common/address/googleGeocodingAddressComponent.ts";
import { GeocodingApi } from "src/api/generated/common/address/geocodingApi.ts";
import i18n from "i18next";
import { GooglePlacesAutocompletePaper } from "src/components/common/googleMaps/GooglePlacesAutocompletePaper.tsx";

export interface AddressAutofillSuggestion {
	place_name: string;
	address_line1?: string;
	address_line2?: string;
	postcode?: string;
	address_level2?: string;
	placeId: string;
}

export interface AavoAddressAutofillProps {
	label: string;
	sx?: SxProps<Theme>;
	error?: string;
	defaultValue?: string;
	onSelectLocation: (value: AddressAutofillSuggestion) => string | undefined;
	onSubmitFreeText: (value: string) => void;
	disabled?: boolean;
}

interface FetchSuggestionsParams {
	input: string;
}

export const AavoAddressAutofill = ({
	label,
	onSelectLocation,
	onSubmitFreeText,
	defaultValue,
	error,
	sx,
	disabled = false,
}: AavoAddressAutofillProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();

	const [inputText, setInputText] = useState<string>("");
	const [value, setValue] = React.useState<string | AddressAutofillSuggestion | null>(defaultValue ?? null);

	const debounceInput = useDebounce();

	const [optionsAsync, fetchOptions] = useAsyncFetch<AddressAutofillSuggestion[], FetchSuggestionsParams>(
		async (params) => {
			try {
				const input = params?.input ?? inputText;
				if (nullIfBlank(input) == null) return [];

				const predictions = await GeocodingApi.searchGooglePlaces({
					input: input,
					types: ["address"],
					language: i18n.language,
				});
				return predictions.map((prediction) => ({
					place_name: prediction.description,
					placeId: prediction.placeId,
				}));
			} catch (e) {
				logErrorAndShowOnDialog(e);
				return [];
			}
		},
		{
			fetchOnMount: false,
		},
	);

	return (
		<Autocomplete
			options={optionsAsync.data || []}
			filterOptions={(x) => x}
			value={value}
			getOptionLabel={(option) => (typeof option === "string" ? option : option.place_name)}
			autoComplete
			freeSolo
			onChange={async (_, value) => {
				if (typeof value === "string") {
					onSubmitFreeText(inputText);
					setValue(value);
				} else if (value === null) {
					onSubmitFreeText("");
					setValue(value);
				} else {
					const suggestion = await fetchSuggestionDetails(value);
					const requestedText = onSelectLocation(suggestion);
					if (requestedText != null) setValue(requestedText);
				}
			}}
			onInputChange={(_, newInputValue) => {
				onInputChanged(newInputValue, true);
			}}
			onBlur={() => {
				if ((value == null || typeof value === "string") && inputText !== "") {
					onSubmitFreeText(inputText);
				}
			}}
			includeInputInList
			filterSelectedOptions
			slots={{
				paper: GooglePlacesAutocompletePaper,
				popper: CustomPopper,
			}}
			sx={mergeSx(sx)}
			disabled={disabled}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						label={label}
						error={error !== undefined}
						helperText={error}
						slotProps={{
							...params.slotProps,
							input: {
								...params.slotProps.input,
								endAdornment: (
									<>
										{optionsAsync.loading && <CircularProgress size={20} color={"inherit"} />}
										{params.slotProps.input.endAdornment}
									</>
								),
							},
						}}
					/>
				);
			}}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				return (
					<li key={key} {...optionProps}>
						<HorizontalBox sx={{ gap: 1.5, marginY: 0.5, alignItems: "center" }}>
							<FontAwesomeIcon icon={faLocationDot} />
							<Typography>{option.place_name}</Typography>
						</HorizontalBox>
					</li>
				);
			}}
		/>
	);

	function onInputChanged(newInput: string, shouldRefreshOptions: boolean) {
		setInputText(newInput);
		if (shouldRefreshOptions)
			debounceInput(200, async () => {
				await fetchOptions({
					input: newInput,
				});
			});
	}
};

async function fetchSuggestionDetails(suggestion: AddressAutofillSuggestion): Promise<AddressAutofillSuggestion> {
	const result = await GeocodingApi.geocodeGooglePlaceId({
		placeId: suggestion.placeId,
		language: i18n.language,
	});
	if (result == null) {
		throw new Error(`Google Geocoder place lookup returned no results for place ID ${suggestion.placeId}.`);
	}
	return {
		...suggestion,
		...parseAddressComponents(result.addressComponents),
		place_name: result.formattedAddress ?? suggestion.place_name,
	};
}

function parseAddressComponents(
	components: GoogleGeocodingAddressComponent[],
): Pick<AddressAutofillSuggestion, "address_line1" | "address_line2" | "postcode" | "address_level2"> {
	const byType = (type: string) => components.find((component) => component.types.includes(type))?.longName;
	const route = byType("route");
	const streetNumber = byType("street_number");

	return {
		address_line1: [route, streetNumber].filter(Boolean).join(" ") || route || streetNumber,
		address_line2: byType("subpremise"),
		postcode: byType("postal_code"),
		address_level2: byType("postal_town") ?? byType("locality") ?? byType("administrative_area_level_3"),
	};
}
